import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { tokens } from '../../../../global/theme/tokens';
import MediaPreview from '../../explorer/MediaPreview';
import ExoExplorer from '../../ExoExplorer';
import ExoDialog from '../../ExoDialog';
import { DashedRow, SectionLabel, formatFileSize } from './taskCardUi';

const MAX_FILE_NAME_LENGTH = 35;
const MAX_FILE_NAME_LENGTH_MOBILE = 10;

// long upload names blow up the row - the full name stays in the tooltip
function shortenFileName(name = '', maxLength) {
  if (name.length <= maxLength) return name;
  return `${name.slice(0, maxLength)}...`;
}

const TaskCardFiles = ({ files = [], onFilesChange }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const [openExplorer, setOpenExplorer] = useState(false);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const maxFileNameLength = isMobile
    ? MAX_FILE_NAME_LENGTH_MOBILE
    : MAX_FILE_NAME_LENGTH;

  // the explorer returns the complete selection, uploads included
  function handleSelect(selectedFiles) {
    setOpenExplorer(false);
    if (onFilesChange) onFilesChange(selectedFiles || []);
  }

  return (
    <Box className="flex flex-col gap-2">
      <SectionLabel>{t('Files')}</SectionLabel>
      <Box
        className="flex flex-col gap-2 rounded-xl p-2"
        sx={{ border: `1px dashed ${colors.text}25` }}
      >
        {files.map((file) => (
          <Box
            key={file.id}
            className="flex items-center gap-2 rounded-lg px-3 py-2"
            sx={{ backgroundColor: colors.bgInput }}
          >
            <MediaPreview
              src={file.thumbnailUrl || file.temporaryUrl}
              mimeType={file.mimeType}
              size="24px"
              allowPreview
            />
            <Tooltip title={file.title}>
              <Typography fontSize="13px" className="grow">
                {shortenFileName(file.title, maxFileNameLength)}
              </Typography>
            </Tooltip>
            <Typography
              fontSize="11px"
              className="opacity-50 whitespace-nowrap"
            >
              {formatFileSize(file.fileSize)}
            </Typography>
            <IconButton
              size="small"
              href={file.temporaryUrl}
              download={file.title}
            >
              <FileDownloadOutlinedIcon sx={{ fontSize: '16px' }} />
            </IconButton>
          </Box>
        ))}

        {onFilesChange && (
          <DashedRow onClick={() => setOpenExplorer(true)}>
            <FileUploadOutlinedIcon sx={{ fontSize: '16px', opacity: 0.6 }} />
            <Typography fontSize="12px" className="opacity-60">
              {t('Add file')}
            </Typography>
          </DashedRow>
        )}
      </Box>

      {/* the explorer the attachments form field uses: pick existing files or
          upload new ones */}
      <ExoDialog open={openExplorer} onClose={() => setOpenExplorer(false)}>
        <ExoExplorer
          onSelect={handleSelect}
          multiSelect
          preSelected={files}
          maxItems={10}
          mediaType="PRIVATE"
        />
      </ExoDialog>
    </Box>
  );
};

export default TaskCardFiles;
